import { client } from 'client/client.gen';

/** Endpoint paths excluded from global unauthorized redirects. */
const AUTH_401_REDIRECT_EXCLUDED_PATHS = new Set<string>([
  '/api/authentication/login',
  '/api/authentication/logout',
  '/api/authentication/current-user',
  '/api/authentication/is-authenticated',
]);

let unauthorizedInterceptorRegistered = false;

/** Resolves request pathname from a Fetch `Request` instance. */
function resolveRequestPathname(request: Request | null): string | null {
  if (request == null) {
    return null;
  }

  try {
    return new URL(request.url).pathname;
  } catch {
    return null;
  }
}

/**
 * Returns `true` when a 401 should trigger the global auth redirect flow.
 */
export function shouldHandleUnauthorizedResponse(
  response: Response | null,
  request: Request | null,
): boolean {
  if (response == null || response.status !== 401) {
    return false;
  }

  const pathname = resolveRequestPathname(request);
  if (pathname == null) {
    return true;
  }

  return !AUTH_401_REDIRECT_EXCLUDED_PATHS.has(pathname);
}

/**
 * Installs a single global 401 interceptor for the generated API client.
 */
export function registerUnauthorizedResponseHandler(onUnauthorized: () => void): void {
  if (unauthorizedInterceptorRegistered) {
    return;
  }

  client.interceptors.error.use((error, response, request) => {
    if (shouldHandleUnauthorizedResponse(response, request)) {
      onUnauthorized();
    }

    return error;
  });

  unauthorizedInterceptorRegistered = true;
}
